import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Listings settings — `/api/admin/listings/settings` (ff-api admin.listing_settings).
 *   GET ?namespace=general → { namespace, settings:{...} }
 *   PUT { namespace?, settings:{...} } (partial merge) → { namespace, settings }
 *
 * The PUT body nests `settings`, so `admin/listings/settings` MUST be in the
 * proxy JSON allowlist or the merge silently no-ops.
 */

const BASE = '/api/admin/listings/settings';

export interface ListingSettingsResponse {
  namespace: string;
  settings: Record<string, any>;
}

export const listingSettingsKeys = {
  all: ['admin-listing-settings'] as const,
  namespace: (ns: string) => [...listingSettingsKeys.all, ns] as const,
};

export const useListingSettingsQuery = (namespace = 'general', enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: listingSettingsKeys.namespace(namespace),
    enabled,
    queryFn: withErrorHandling(
      () => request<ListingSettingsResponse>(`${BASE}?namespace=${encodeURIComponent(namespace)}`),
      'Failed to load listing settings'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useUpdateListingSettingsMutation = () => {
  const request = useClientStore((s) => s.request);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: withErrorHandling(
      (vars: { namespace?: string; settings: Record<string, any> }) =>
        request<ListingSettingsResponse>(BASE, 'PUT', {
          ...(vars.namespace ? { namespace: vars.namespace } : {}),
          settings: vars.settings,
        }),
      'Failed to save listing settings'
    ),
    onSuccess: (data: any) => {
      if (data?.namespace) {
        qc.setQueryData(listingSettingsKeys.namespace(data.namespace), data);
      }
      qc.invalidateQueries({ queryKey: listingSettingsKeys.all });
    },
  });
};
